import { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import {
  X, ShieldCheck, AlertTriangle, Loader2, FileText, Layers,
  PenLine, Bot, ScrollText, ChevronRight, ChevronDown, Filter,
  Hash, Calendar, ArrowDownRight, ArrowUpRight, Sparkles,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";

const formatMoney = (n, ccy = "GBP") => {
  const sym = { GBP: "£", USD: "$", EUR: "€", AED: "د.إ" }[ccy] || ccy + " ";
  const sign = n < 0 ? "-" : "";
  return `${sign}${sym}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return String(d); }
};

const FILTERS = [
  { v: "all",                 l: "All" },
  { v: "ai_or_rules",         l: "AI / Rules" },
  { v: "manual",              l: "Manual" },
  { v: "uploaded_file",       l: "Uploaded Files" },
  { v: "bank_feed",           l: "Bank Feed" },
  { v: "accounting_sync",     l: "Accounting Sync" },
  { v: "inflows",             l: "Inflows" },
  { v: "outflows",            l: "Outflows" },
];

function rowMatchesFilter(row, filter) {
  switch (filter) {
    case "all":             return true;
    case "ai_or_rules":     return !row.manual_override;
    case "manual":          return !!row.manual_override;
    case "uploaded_file":   return row.source_type === "uploaded_file";
    case "bank_feed":       return row.source_type === "bank_feed";
    case "accounting_sync": return row.source_type === "accounting_sync";
    case "inflows":         return row.amount > 0;
    case "outflows":        return row.amount < 0;
    default:                return true;
  }
}

export default function VaultDetailDrawer({ vaultType, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!vaultType) return;
    setLoading(true); setError(null); setExpandedId(null); setFilter("all");
    api.get(`/dashboard/vaults/${vaultType}/detail`)
      .then((r) => setDetail(r.data))
      .catch((e) => setError(e.response?.data?.message || "Failed to load breakdown"))
      .finally(() => setLoading(false));
  }, [vaultType]);

  const filteredRows = useMemo(() => {
    if (!detail?.breakdown) return [];
    return detail.breakdown.filter((row) => rowMatchesFilter(row, filter));
  }, [detail, filter]);

  if (!vaultType) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="vault-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-4xl bg-[#0a1628] border-l border-slate-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a1628]/95 backdrop-blur border-b border-slate-800 px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-manrope">Vault Breakdown</p>
              <h2 className="text-2xl font-barlow font-bold text-slate-100 mt-1" data-testid="vault-drawer-title">
                {detail?.vault_label || vaultType.replace(/_/g, " ")}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Every amount in this vault is traced back to its source record, allocation reason, and audit history.
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-200 p-2 -mr-2"
              data-testid="vault-drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {detail?.status === "success" && (
            <div className="mt-4 flex items-baseline gap-3">
              <div className="text-3xl font-mono tabular font-bold text-slate-50" data-testid="vault-drawer-total">
                {formatMoney(detail.total, detail.reporting_currency)}
              </div>
              <div className="text-xs text-slate-500">
                {detail.audit.entries_count} entries · {detail.audit.ai_allocations_count} AI · {detail.audit.manual_adjustments_count} manual
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-20" data-testid="vault-drawer-loading">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">{error}</div>
          )}

          {detail && !loading && detail.status === "success" && (
            <>
              {/* 1. Vault Meaning */}
              <section data-testid="vault-drawer-meaning">
                <SectionHeader icon={ShieldCheck} title="Vault Meaning" />
                <p className="text-sm text-slate-300 leading-relaxed">{detail.meaning}</p>
                {detail.risk_if_misused && (
                  <div className="mt-2 flex gap-2 text-xs text-amber-300/90 bg-amber-500/[0.04] border border-amber-500/20 rounded px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{detail.risk_if_misused}</span>
                  </div>
                )}
              </section>

              {/* 2. Empty-state with examples */}
              {detail.breakdown.length === 0 && detail.examples?.length > 0 && (
                <section data-testid="vault-drawer-examples">
                  <SectionHeader icon={Sparkles} title="What kind of records belong here" />
                  <p className="text-xs text-slate-500 mb-2">
                    No records have been allocated to this vault yet. Examples of what would belong:
                  </p>
                  <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
                    {detail.examples.map((ex, i) => (
                      <div key={i} className="px-3 py-2.5 flex justify-between items-center"
                        data-testid={`vault-example-${i}`}>
                        <div>
                          <div className="text-sm text-slate-300">{ex.description}</div>
                          <div className="text-[11px] text-slate-500">{ex.counterparty}</div>
                        </div>
                        <Badge className="text-[9px] bg-slate-800/60 text-slate-400 border-none">example</Badge>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* 3. Grouped Breakdown Cards */}
              {detail.groups?.length > 0 && (
                <section data-testid="vault-drawer-groups">
                  <SectionHeader icon={Layers} title={
                    vaultType === "TAX_CUSTODY" ? "Tax Subcategories"
                    : vaultType === "OPERATING_CUSTODY" ? "Operating Categories"
                    : vaultType === "FREE_EQUITY" ? "Income Types"
                    : "Grouped Breakdown"
                  } />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {detail.groups.map((g) => (
                      <div key={g.group_label}
                        className="rounded-lg border border-slate-800 bg-slate-900/30 p-3"
                        data-testid={`vault-group-${g.group_label.replace(/\s+/g, '-')}`}>
                        <div className="flex items-baseline justify-between">
                          <div className="text-sm font-semibold text-slate-200">{g.group_label}</div>
                          <div className={`text-sm font-mono tabular font-semibold ${g.amount < 0 ? "text-red-400" : "text-emerald-400"}`}>
                            {formatMoney(g.amount, detail.reporting_currency)}
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">{g.entries_count} {g.entries_count === 1 ? "record" : "records"}</div>
                        {g.summary && (
                          <p className="text-[11px] text-slate-400 mt-1.5 leading-relaxed">{g.summary}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* 4. Filters */}
              {detail.breakdown.length > 0 && (
                <section data-testid="vault-drawer-filters">
                  <div className="flex items-center gap-2 mb-2">
                    <Filter className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-manrope">Filter</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {FILTERS.map(f => (
                      <button
                        key={f.v}
                        onClick={() => setFilter(f.v)}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                          filter === f.v
                            ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                            : "bg-slate-900/40 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-300"
                        }`}
                        data-testid={`vault-filter-${f.v}`}
                      >
                        {f.l}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* 5. Source Records */}
              <section data-testid="vault-drawer-records">
                <SectionHeader icon={FileText} title={`Source Records Making Up This Vault (${filteredRows.length}${filter !== 'all' ? ' of ' + detail.breakdown.length : ''})`} />

                {filteredRows.length === 0 && detail.breakdown.length > 0 && (
                  <div className="rounded-lg border border-slate-800 px-4 py-6 text-center text-sm text-slate-500">
                    No records match the selected filter.
                  </div>
                )}

                {filteredRows.length > 0 && (
                  <div className="rounded-lg border border-slate-800 overflow-hidden">
                    {filteredRows.map((row) => (
                      <RecordRow
                        key={row.ledger_entry_id}
                        row={row}
                        currency={detail.reporting_currency}
                        expanded={expandedId === row.ledger_entry_id}
                        onToggle={() => setExpandedId(expandedId === row.ledger_entry_id ? null : row.ledger_entry_id)}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* 6. Audit Summary */}
              <section data-testid="vault-drawer-audit">
                <SectionHeader icon={ScrollText} title="Audit Summary" />
                <div className="grid grid-cols-3 gap-3">
                  <AuditTile icon={FileText} label="Total entries" value={detail.audit.entries_count} />
                  <AuditTile icon={Bot} label="AI / rules" value={detail.audit.ai_allocations_count} />
                  <AuditTile icon={PenLine} label="Manual" value={detail.audit.manual_adjustments_count} />
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-cyan-400" />
      <h3 className="text-xs uppercase tracking-wider font-manrope text-slate-400">{title}</h3>
    </div>
  );
}

function SourceBadge({ label }) {
  const m = {
    "AI Allocation":     "bg-violet-500/15 text-violet-300",
    "Rules Engine":      "bg-cyan-500/15 text-cyan-300",
    "Manual Entry":      "bg-amber-500/15 text-amber-300",
    "Manual Adjustment": "bg-orange-500/15 text-orange-300",
  };
  return (
    <Badge className={`text-[9px] font-medium border-none ${m[label] || "bg-slate-700/30 text-slate-300"}`}>
      {label}
    </Badge>
  );
}

function AuditTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-mono tabular font-bold text-slate-100 mt-1">{value}</div>
    </div>
  );
}

function RecordRow({ row, currency, expanded, onToggle }) {
  const isInflow = row.amount > 0;
  return (
    <div className="border-b border-slate-800 last:border-b-0" data-testid={`vault-record-${row.ledger_entry_id}`}>
      {/* Compact row */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-3 hover:bg-slate-900/40 transition-colors text-left flex items-start gap-3"
        data-testid={`vault-record-toggle-${row.ledger_entry_id}`}
      >
        <div className="mt-1">
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <SourceBadge label={row.source_label} />
            {row.confidence != null && (
              <span className="text-[9px] text-slate-500 font-mono">conf {(row.confidence * 100).toFixed(0)}%</span>
            )}
            <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
              <Calendar className="w-2.5 h-2.5" />{fmtDate(row.date)}
            </span>
            {row.source_file && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <FileText className="w-2.5 h-2.5" />{row.source_file}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-200 font-medium truncate">{row.description}</div>
          <div className="flex flex-wrap items-center gap-x-3 mt-1 text-[11px] text-slate-400">
            {row.counterparty && <span>{row.counterparty}</span>}
            {row.reference && <span><Hash className="w-2.5 h-2.5 inline" /> {row.reference}</span>}
            {(row.reason_text || row.reason_code) && (
              <span className="italic truncate">{row.reason_text || row.reason_code}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <div className={`text-sm font-mono tabular font-semibold whitespace-nowrap inline-flex items-center gap-1 ${isInflow ? "text-emerald-400" : "text-red-400"}`}>
            {isInflow ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {formatMoney(row.amount, row.currency || currency)}
          </div>
          <div className="text-[9px] text-slate-500 mt-0.5">{row.direction}</div>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="bg-slate-900/30 border-t border-slate-800 px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]"
          data-testid={`vault-record-detail-${row.ledger_entry_id}`}>
          <DetailBlock label="Original Imported Data">
            <KV k="raw description" v={row.original_record?.description || row.original_record?.user_input_amount ? JSON.stringify(row.original_record?.description || "") : "—"} />
            <KV k="original amount" v={row.original_record?.user_input_amount ?? row.original_record?.amount ?? "—"} />
            <KV k="original currency" v={row.original_record?.user_input_currency ?? row.original_record?.currency ?? "—"} />
            <KV k="source" v={row.source_type === "manual_entry" ? "Manual entry" : (row.source_file ? `File: ${row.source_file}` : (row.source_type || "uploaded"))} />
          </DetailBlock>

          <DetailBlock label="Normalized Record">
            <KV k="date" v={fmtDate(row.normalized_record?.date)} />
            <KV k="counterparty" v={row.normalized_record?.counterparty || "—"} />
            <KV k="direction" v={row.normalized_record?.direction || "—"} />
            <KV k="reporting amount" v={`${row.currency || currency} ${(row.normalized_record?.reporting_amount ?? Math.abs(row.amount)).toLocaleString()}`} />
            <KV k="reporting currency" v={row.normalized_record?.reporting_currency || row.currency || currency} />
            {row.normalized_record?.fx_rate ? <KV k="fx rate" v={row.normalized_record.fx_rate} /> : null}
          </DetailBlock>

          <DetailBlock label="Allocation Explanation">
            <KV k="assigned vault" v={row.normalized_record?.recommended_vault || row.source_label} />
            <KV k="reason code" v={row.reason_code || "—"} />
            <KV k="explanation" v={row.reason_text || "—"} long />
            <KV k="classification" v={row.classification_source || "—"} />
            <KV k="confidence" v={row.confidence != null ? `${(row.confidence * 100).toFixed(0)}%` : "—"} />
          </DetailBlock>

          <DetailBlock label="Audit History">
            {(row.history || []).length === 0 ? (
              <div className="text-slate-500">No history events.</div>
            ) : (
              <ul className="space-y-1">
                {(row.history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300">
                    <span className="w-1 h-1 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-200 capitalize">{(h.type || "event").replace(/_/g, " ")}</div>
                      <div className="text-[10px] text-slate-500">
                        {fmtDate(h.created_at)} {h.actor && `• ${h.actor.slice(0, 8)}…`}
                      </div>
                      {h.reason && <div className="text-[10px] text-slate-400 italic">{h.reason}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DetailBlock>
        </div>
      )}
    </div>
  );
}

function DetailBlock({ label, children }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/50 p-3">
      <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-manrope mb-2">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ k, v, long }) {
  return (
    <div className={`flex ${long ? "flex-col" : "items-baseline justify-between gap-2"}`}>
      <span className="text-slate-500 capitalize">{k}</span>
      <span className={`text-slate-300 ${long ? "mt-0.5" : "text-right truncate"}`}>{String(v ?? "—")}</span>
    </div>
  );
}
